"use client"

import { useId } from "react"

import { LinkButton, Text } from "@/components/ui"

import { HeadingMark } from "../layout/heading-mark"

interface FaqItem {
  question: string
  answer: string
}

interface FaqBandProps {
  title: string
  accent?: string
  sub?: string
  items: FaqItem[]
  cta?: string
  ctaHref?: string
}

// Two columns on desktop (the ask on the left, the answers on the right), a
// single stack on mobile. Each row is its own rounded card lifted off the page,
// not a hairline-divided list. Native details/summary, so the rows open with
// JavaScript off. The exclusive `name` means opening one closes another.
export const FaqBand = ({ title, accent, sub, items, cta, ctaHref }: FaqBandProps) => {
  const group = useId()
  return (
    <div className="@container">
      <div className="@3xl:grid-cols-dudas grid items-start gap-500 @3xl:gap-3200">
        <div className="flex flex-col items-start">
          <h2 className="font-text text-section-head max-w-title text-fg-strong text-balance">
            {title}
            {accent && <HeadingMark>{accent}</HeadingMark>}
          </h2>
          {sub && (
            <Text as="p" variant="bodyLg" family="text" tone="default" className="max-w-measure mt-400">
              {sub}
            </Text>
          )}
          {cta && (
            <LinkButton href={ctaHref} variant="primary" className="lift bg-brand mt-500 inline-flex @3xl:mt-750">
              {cta}
            </LinkButton>
          )}
        </div>
        <ul className="flex flex-col gap-250">
          {items.map((item, index) => (
            <li key={item.question}>
              <details name={group} open={index === 0} className="group disclosure rounded-8x bg-surface px-500 py-500">
                <summary className="focus-visible:ring-brand rounded-4x flex cursor-pointer list-none items-center gap-400 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none pointer-coarse:min-h-11">
                  <Text as="span" variant="bodyLg" family="text" tone="strong" className="flex-1 font-medium">
                    {item.question}
                  </Text>
                  <span aria-hidden className="text-brand relative grid size-6 shrink-0 place-items-center">
                    <span className="bg-brand absolute h-px w-4 rounded-full" />
                    <span className="bg-brand duration-quick ease-productive absolute h-px w-4 rotate-90 rounded-full transition-transform group-open:rotate-0" />
                  </span>
                </summary>
                <Text as="p" variant="body" family="text" tone="default" className="@2xl:text-body-lg pt-400 whitespace-pre-line @2xl:pt-500">
                  {item.answer}
                </Text>
              </details>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
